import React, { useEffect, useState, useMemo, useRef } from 'react';
import api from '../../api';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import SuccessModal from '../../components/common/SuccessModal';
import FailModal from '../../components/common/FailModal';
import { usePagination } from '../../hooks/usePagination';
import { useSelection } from '../../hooks/useSelection';
import { validateField } from '../../utils/validation';
import { useAuth } from '../../contexts/AuthProvider';
import { GenericView, SearchInput } from '../../components/common/table';

const Branch = () => {
  // Actions dropdown refs for manual Bootstrap Dropdown initialization
  const actionsToggleRef = useRef(null);
  const actionsContainerRef = useRef(null);
  const actionsDropdownInstanceRef = useRef(null);
  const [isActionsOpen, setIsActionsOpen] = useState(false);

  useEffect(() => {
    const toggleEl = actionsToggleRef.current;
    const containerEl = actionsContainerRef.current;
    if (!toggleEl || !containerEl) return;
    if (typeof window !== 'undefined' && window.bootstrap?.Dropdown) {
      const instance = new window.bootstrap.Dropdown(toggleEl, { autoClose: true });
      actionsDropdownInstanceRef.current = instance;
      const onShow = () => setIsActionsOpen(true);
      const onHide = () => setIsActionsOpen(false);
      containerEl.addEventListener('show.bs.dropdown', onShow);
      containerEl.addEventListener('hide.bs.dropdown', onHide);
      return () => {
        containerEl.removeEventListener('show.bs.dropdown', onShow);
        containerEl.removeEventListener('hide.bs.dropdown', onHide);
        try { instance.dispose(); } catch {}
        actionsDropdownInstanceRef.current = null;
      };
    }
  }, []);

  // Permission system
  const { user, can, capabilitiesLoading } = useAuth();
  const canBranches = (key) => can(key, 'branches');

    const pagination = usePagination(10);

    const [search, setSearch] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');

    const handleSearchInputChange = (value) => {
        setSearch(value);
        setSearchTerm(value);
    };

    const handleSearchSubmit = () => {
        setAppliedSearchTerm(searchTerm);
        pagination.setPage(0);
    };
    const [items, setItems] = useState([]);
    const [formData, setFormData] = useState({ name: '', address: '', phone: '' });
    const [editingId, setEditingId] = useState(null);
    const [errors, setErrors] = useState({});
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [confirmAction, setConfirmAction] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showFailModal, setShowFailModal] = useState(false);
    const [failMessage, setFailMessage] = useState('');

    const {
        selectedIds,
        setSelectedIds,
        handleCheckboxClick,
        handleSelectAll
    } = useSelection();

    const fetchBranchList = async () => {
        try {
            setIsLoading(true);
            const response = await api.get('/branches');
            const data = response.data?.data ?? response.data ?? [];
            const list = Array.isArray(data) ? data : [];
            setItems(list);
        } catch (error) {
            console.error('Error fetching Branch list:', error);
            let errorMessage = 'Failed to fetch Branch list.';
            let technicalDetails = '';
            if (error.response) {
                switch (error.response.status) {
                    case 403:
                        errorMessage = 'You do not have permission to view Branch.';
                        technicalDetails = 'Required role or permission is missing';
                        break;
                    case 500:
                        errorMessage = 'Server error while fetching Branch.';
                        technicalDetails = error.response.data?.message || 'Internal server error occurred';
                        break;
                    default:
                        errorMessage = 'An error occurred while fetching Branch.';
                        technicalDetails = `Server responded with status: ${error.response.status}`;
                }
            } else if (error.request) {
                errorMessage = 'Unable to connect to the server.';
                technicalDetails = 'Network error or server is not responding';
            } else {
                technicalDetails = error.message;
            }
            setItems([]);
            setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
            setShowFailModal(true);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchBranchList();
    }, []);

    const filteredItems = useMemo(() => {
        if (!appliedSearchTerm.trim()) return items;
        const q = appliedSearchTerm.toLowerCase().trim();
        return items.filter(
            (item) =>
                (item.name || '').toLowerCase().includes(q) ||
                (item.address || '').toLowerCase().includes(q) ||
                (item.phone || '').toLowerCase().includes(q)
        );
    }, [items, appliedSearchTerm]);

    const totalFiltered = filteredItems.length;
    const totalPages = Math.max(1, Math.ceil(totalFiltered / pagination.size));
    const displayedItems = useMemo(
        () =>
            filteredItems.slice(
                pagination.page * pagination.size,
                pagination.page * pagination.size + pagination.size
            ),
        [filteredItems, pagination.page, pagination.size]
    );

    useEffect(() => {
        pagination.setTotal(totalFiltered);
        pagination.setTotalPages(totalPages);
        pagination.setCurrentPageItems(displayedItems.length);
    }, [totalFiltered, totalPages, displayedItems.length]);

    const handleCreate = () => {
    if (!canBranches('create')) { setFailMessage('You do not have permission to create.'); setShowFailModal(true); return; }
        setFormData({ name: '', address: '', phone: '' });
        setEditingId(null);
        setErrors({});
        const modalEl = document.getElementById('modal-branch');
        if (window.bootstrap && window.bootstrap.Modal && modalEl) {
            const modal = new window.bootstrap.Modal(modalEl);
            modal.show();
        }
    };

    const handleEdit = (item) => {
    if (!canBranches('edit')) { setFailMessage('You do not have permission to edit.'); setShowFailModal(true); return; }
        setFormData({
            name: item.name || '',
            address: item.address || '',
            phone: item.phone || ''
        });
        setEditingId(item.id);
        setErrors({});
        setTimeout(() => {
            const modalEl = document.getElementById('modal-branch');
            if (window.bootstrap && window.bootstrap.Modal && modalEl) {
                const modal = new window.bootstrap.Modal(modalEl);
                modal.show();
            }
        }, 100);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        const error =
            name === 'name' ? validateField('name', value, { required: true }) : null;
        setErrors((prev) => ({ ...prev, [name]: error }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const nameError = validateField('name', formData.name, { required: true });
        setErrors({ name: nameError, address: null, phone: null });
        if (nameError) return;
        try {
            const payload = {
                name: formData.name.trim(),
                address: formData.address?.trim() || null,
                phone: formData.phone?.trim() || null
            };
            if (editingId) {
                await api.put(`/branches/${editingId}`, payload);
                setSuccessMessage('Branch has been successfully updated.');
            } else {
                await api.post('/branches', payload);
                setSuccessMessage('Branch has been successfully added.');
            }
            setShowSuccessModal(true);
            setEditingId(null);
            setFormData({ name: '', address: '', phone: '' });
            setErrors({});
            await fetchBranchList();
            const modalEl = document.getElementById('modal-branch');
            const bootstrapModal = window.bootstrap?.Modal.getInstance(modalEl);
            if (bootstrapModal) bootstrapModal.hide();
        } catch (error) {
            console.error('Error saving Branch:', error);
            let errorMessage = 'Failed to save Branch.';
            let technicalDetails = '';
            if (error.response) {
                switch (error.response.status) {
                    case 409:
                    case 400:
                        errorMessage =
                            error.response.data?.message ||
                            'A Branch with this name may already exist or invalid data.';
                        technicalDetails = error.response.data?.message || '';
                        break;
                    case 403:
                        errorMessage = 'You do not have permission to perform this action.';
                        technicalDetails = 'Required role or permission is missing for this operation';
                        break;
                    case 404:
                        errorMessage = 'Branch not found.';
                        technicalDetails = 'The requested item may have been deleted or does not exist';
                        break;
                    case 500:
                        errorMessage = 'Server encountered an error while processing the request.';
                        technicalDetails = error.response.data?.message || 'Internal server error occurred';
                        break;
                    default:
                        technicalDetails = `Server responded with status: ${error.response.status}`;
                }
            } else if (error.request) {
                errorMessage = 'Unable to connect to the server.';
                technicalDetails = 'Network error or server is not responding';
            } else {
                technicalDetails = error.message;
            }
            setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
            setShowFailModal(true);
        }
    };

    const editSelected = () => {
        if (Array.isArray(selectedIds) && selectedIds.length !== 1) {
            setSuccessMessage('Please select exactly one item to edit');
            setShowSuccessModal(true);
            return;
        }
        const selectedId = Array.isArray(selectedIds) ? selectedIds[0] : null;
        const selectedItem = items.find((item) => item.id === selectedId);
        if (selectedItem) handleEdit(selectedItem);
    };

    const deleteSelected = () => {
    if (!canBranches('delete')) { setFailMessage('You do not have permission to delete.'); setShowFailModal(true); return; }
        if (Array.isArray(selectedIds) && selectedIds.length === 0) {
            setFailMessage(
                'Please select at least one Branch to delete.\n\nTechnical Details: No items selected for deletion'
            );
            setShowFailModal(true);
            return;
        }
        setConfirmAction(() => async () => {
            try {
                await Promise.all(selectedIds.map((id) => api.delete(`/branches/${id}`)));
                setSelectedIds([]);
                fetchBranchList();
                setShowDeleteConfirmation(false);
                setSuccessMessage('Selected Branch items deleted successfully');
                setShowSuccessModal(true);
            } catch (error) {
                console.error('Error deleting Branch:', error);
                let errorMessage = 'Failed to delete Branch.';
                let technicalDetails = '';
                if (error.response) {
                    switch (error.response.status) {
                        case 403:
                            errorMessage = 'You do not have permission to delete these items.';
                            technicalDetails = 'Required role or permission is missing for deletion';
                            break;
                        case 400:
                            errorMessage = 'Invalid delete request.';
                            technicalDetails =
                                error.response.data?.message || 'The server rejected the deletion request';
                            break;
                        case 404:
                            errorMessage = 'One or more items not found.';
                            technicalDetails = 'Some items may have been already deleted';
                            break;
                        case 500:
                            errorMessage = 'Server error while deleting items.';
                            technicalDetails = error.response.data?.message || 'Internal server error during deletion';
                            break;
                        default:
                            technicalDetails = `Server responded with status: ${error.response.status}`;
                    }
                } else if (error.request) {
                    errorMessage = 'Unable to connect to the server.';
                    technicalDetails = 'Network error or server is not responding';
                } else {
                    technicalDetails = error.message;
                }
                setShowDeleteConfirmation(false);
                setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
                setShowFailModal(true);
            }
        });
        setShowDeleteConfirmation(true);
    };

    useEffect(() => {
        const modal = document.getElementById('modal-branch');
        const onHidden = () => {
            setFormData({ name: '', address: '', phone: '' });
            setEditingId(null);
        };
        modal?.addEventListener('hidden.bs.modal', onHidden);
        return () => modal?.removeEventListener('hidden.bs.modal', onHidden);
    }, []);

    const formatDate = (val) => (val ? new Date(val).toLocaleString() : '-');

    const columns = [
        { key: 'name', label: 'Name', sortable: true, render: (value) => value || '-' },
        { key: 'address', label: 'Address', sortable: true, render: (value) => value || '-' },
        { key: 'phone', label: 'Phone', sortable: true, render: (value) => value || '-' },
        { key: 'createdAt', label: 'Created Date', sortable: true, render: (value, row) => formatDate(value || row.created_at) },
        { key: 'updatedAt', label: 'Updated Date', sortable: true, render: (value, row) => formatDate(value || row.updated_at) },
    ];

    return (
        <>
            <div className="page-header d-print-none">
                <div className="container-xl">
                    <div className="row g-2 align-items-center">
                        <div className="col">
                            <h2 className="page-title d-flex align-items-center">Branch</h2>
                        </div>
                        <div className="col-auto ms-auto d-print-none">
                            <div className="btn-list">
                                <a
                                    href="#"
                                    className="btn btn-primary btn-5 d-none d-sm-inline-block"
                                    onClick={handleCreate}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width={24}
                                        height={24}
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="icon icon-2"
                                    >
                                        <path d="M12 5l0 14" />
                                        <path d="M5 12l14 0" />
                                    </svg>
                                    Create
                                </a>
                                <a
                                    href="#"
                                    className="btn btn-primary btn-6 d-sm-none btn-icon"
                                    onClick={handleCreate}
                                    aria-label="Create new branch"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width={24}
                                        height={24}
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="icon icon-2"
                                    >
                                        <path d="M12 5l0 14" />
                                        <path d="M5 12l14 0" />
                                    </svg>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body">
                <div className="container-xl">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-body">
                                <GenericView
                                    data={displayedItems}
                                    columns={columns}
                                    isLoading={isLoading || capabilitiesLoading}
                                    emptyStateMessage="No branches found"
                                    checkboxSelection={true}
                                    selectedRows={selectedIds}
                                    onSelectRow={(itemId, isSelected) => {
                                        if (isSelected) {
                                            setSelectedIds([...selectedIds, itemId]);
                                        } else {
                                            setSelectedIds(selectedIds.filter(id => id !== itemId));
                                        }
                                    }}
                                    onSelectAllRows={(itemIds) => {
                                        if (Array.isArray(itemIds) && itemIds.length > 0) {
                                            setSelectedIds([...new Set([...selectedIds, ...itemIds])]);
                                        } else {
                                            setSelectedIds([]);
                                        }
                                    }}
                                    onEdit={canBranches('edit') ? (item) => {
                                        setSelectedIds([item.id]);
                                        handleEdit(item);
                                    } : undefined}
                                    onDelete={canBranches('delete') ? (item) => {
                                        setSelectedIds([item.id]);
                                        deleteSelected();
                                    } : undefined}
                                    searchComponent={
                                        <div className="d-flex gap-2">
                                            <SearchInput
                                                value={searchTerm}
                                                onChange={handleSearchInputChange}
                                                onKeyPress={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        handleSearchSubmit();
                                                    }
                                                }}
                                                placeholder="Search name, address, phone..."
                                                uniqueId="branch-search"
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                onClick={handleSearchSubmit}
                                            >
                                                Search
                                            </button>
                                        </div>
                                    }
                                    actionButtons={
                                        <div className="d-flex gap-2">
                                            {(canBranches('edit') || canBranches('delete')) && (
                                                <div ref={actionsContainerRef} className="dropdown">
                                                    <a
                                                        ref={actionsToggleRef}
                                                        href="#"
                                                        className="btn dropdown-toggle"
                                                        data-bs-toggle="dropdown"
                                                        aria-expanded={isActionsOpen}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            const inst = actionsDropdownInstanceRef.current;
                                                            if (inst?.toggle) inst.toggle();
                                                            else setIsActionsOpen(prev => !prev);
                                                        }}
                                                    >Actions</a>
                                                    <div className="dropdown-menu">
                                                        {canBranches('edit') && (
                                                            <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); editSelected(); }}
                                                                style={{ color: selectedIds.length === 1 ? "" : "#ccc", pointerEvents: selectedIds.length === 1 ? "auto" : "none" }}>
                                                                Edit
                                                            </a>
                                                        )}
                                                        {canBranches('delete') && (
                                                            <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); deleteSelected(); }}
                                                                style={{ color: selectedIds.length ? "" : "#ccc", pointerEvents: selectedIds.length ? "auto" : "none" }}>
                                                                Delete
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    }
                                    pagination={{
                                        currentPage: pagination.page + 1,
                                        totalRows: pagination.total,
                                        rowsPerPage: pagination.size,
                                        onPageChange: (page) => pagination.setPage(page - 1),
                                        onRowsPerPageChange: (size) => {
                                            pagination.setSize(size);
                                            pagination.setPage(0);
                                        }
                                    }}
                                    pageSizeOptions={[10, 25, 50, 100]}
                                    uniqueId="branch-table"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div
                className="modal modal-blur fade"
                id="modal-branch"
                tabIndex={-1}
                role="dialog"
                aria-hidden="true"
            >
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                {editingId ? 'Edit Branch' : 'Create Branch'}
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            ></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-lg-12">
                                        <div className="mb-3">
                                            <label className="form-label required">Name</label>
                                            <input
                                                type="text"
                                                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                                id="name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                placeholder="Enter branch name"
                                            />
                                            {errors.name && (
                                                <div className="invalid-feedback">{errors.name}</div>
                                            )}
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Address</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="address"
                                                name="address"
                                                value={formData.address}
                                                onChange={handleInputChange}
                                                placeholder="Enter address"
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Phone</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="phone"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                placeholder="Enter phone"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <a href="#" className="btn btn-link link-secondary btn-3" data-bs-dismiss="modal">
                                    Cancel
                                </a>
                                <button type="submit" className="btn btn-primary ms-auto">
                                    {editingId ? 'Update' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <ConfirmationModal
                isOpen={showDeleteConfirmation}
                onClose={() => setShowDeleteConfirmation(false)}
                onConfirm={confirmAction || (() => {})}
                title="Delete Confirmation"
                message={
                    Array.isArray(selectedIds) && selectedIds.length > 0
                        ? 'Are you sure you want to delete the selected Branch items? This action cannot be undone.'
                        : 'Are you sure you want to delete this Branch? This action cannot be undone.'
                }
                confirmText="Delete"
                cancelText="Cancel"
                type="danger"
            />
            <SuccessModal
                isOpen={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                title="Success"
                message={successMessage}
                buttonText="OK"
            />
            <FailModal
                isOpen={showFailModal}
                onClose={() => setShowFailModal(false)}
                title="Error"
                message={failMessage}
                buttonText="OK"
            />
        </>
    );
};

export default Branch;
